import {
  type LifecycleEntitlement,
  type WpMode,
  wpModeMovesTowardEnforce,
} from '@guard-dog/contracts';
import type { EdgeMode } from '../api/client';

const DAY_MS = 24 * 60 * 60 * 1000;

export function allowsPaidSetup(entitlement: LifecycleEntitlement | null | undefined): boolean {
  return entitlement?.status === 'trialing' || entitlement?.status === 'active';
}

export function daysRemaining(
  entitlement: LifecycleEntitlement | null | undefined,
  now: number | Date = Date.now(),
): number | null {
  if (!entitlement?.trialEndsAt) return null;
  const end = Date.parse(entitlement.trialEndsAt);
  if (!Number.isFinite(end)) return null;
  const nowMs = typeof now === 'number' ? now : now.getTime();
  return Math.max(0, Math.ceil((end - nowMs) / DAY_MS));
}

export function canMoveToEdgeMode(input: {
  current?: EdgeMode;
  next: EdgeMode;
  canAddProtection: boolean;
}): boolean {
  return input.next !== 'enforce' || input.current === 'enforce' || input.canAddProtection;
}

/**
 * The WP-plane equivalent. Same rule as `canMoveToEdgeMode` — only **arriving at `enforce`** is
 * gated — expressed by deferring to the contract rather than restating it.
 *
 * The deferral is the point. An earlier draft of `wpModeMovesTowardEnforce` ranked
 * `off < monitor < enforce`, which made `off → monitor` an escalation: a lapsed tenant who turned a
 * site off to answer "is Guard Dog doing this?" during an incident could not turn it back on, while
 * every untouched site they owned was already monitoring, because an unset column resolves to
 * `monitor` with no transition to gate. Had this file restated the rule instead of calling it, the
 * fix in the contract would have left the console still refusing the button (ADR-0030 §5).
 *
 * A console that offers a button the api answers 403 to is worse than one that shows it paused; a
 * console that pauses a button the api would have allowed is worse than both.
 */
export function canMoveToWpMode(input: {
  current?: WpMode;
  next: WpMode;
  canAddProtection: boolean;
}): boolean {
  return !wpModeMovesTowardEnforce(input.current, input.next) || input.canAddProtection;
}

export function describeEntitlement(
  entitlement: LifecycleEntitlement | null | undefined,
  now: number | Date = Date.now(),
): {
  tone: 'ok' | 'warn';
  statusLabel: string;
  title: string;
  body: string;
  restricted: boolean;
} {
  if (!entitlement) {
    return {
      tone: 'warn',
      statusLabel: 'restricted',
      title: 'Plan status unavailable',
      body: 'New site enrollment and additive protection changes are paused. Existing protection and safety actions remain available.',
      restricted: true,
    };
  }

  if (entitlement.status === 'active') {
    return {
      tone: 'ok',
      statusLabel: `${entitlement.plan} active`,
      title: `${entitlement.plan.toUpperCase()} plan active`,
      body: 'You can connect sites, author rules, and manage edge protection.',
      restricted: false,
    };
  }

  if (entitlement.status === 'trialing') {
    const days = daysRemaining(entitlement, now);
    const left = days === null ? 'trial active' : `${days} day${days === 1 ? '' : 's'} remaining`;
    return {
      tone: 'ok',
      statusLabel: 'trialing',
      title: `${entitlement.plan.toUpperCase()} trial — ${left}`,
      body: 'Enjoy the good stuff while the trial is active: connect sites, author rules, and evaluate edge protection.',
      restricted: false,
    };
  }

  return {
    tone: 'warn',
    statusLabel: entitlement.status,
    title:
      entitlement.status === 'expired'
        ? 'Trial expired — setup actions paused'
        : 'Plan restricted — setup actions paused',
    body: 'New site enrollment, rule authoring, and moving edge protection toward enforce are paused. Existing sites keep syncing; you can still disable rules, move toward monitor/bypass, remove sites, and contact us to upgrade.',
    restricted: true,
  };
}
